/**
 * Detail VM for טיסה + מלון room selection.
 * Live: Hubwayz room offers for hotel + LDS flight options.
 * Demo fallback: fixtures only when live context is missing.
 */

import { MOCK_PACKAGES } from "../packagesResultsMockup/mockPackagesData";
import { MOCK_PROFILE_PACKAGE } from "../packageProfileMockup/mockPackageProfileData";
import { displayBoardBasisHe } from "./displayBoardBasisHe";
import { formatInternalFreeCancellationLabel } from "./hubwayz/hubwayzCancellationDisplay";
import {
  formatSupplierBeddingLabel,
  readExplicitBedding,
} from "./hubwayzBeddingLabel";
import {
  normalizeUnifiedFlightLeg,
  resolveUnifiedFlightLegs,
} from "./unifiedFlightLegs";

const HUBWAYZ_IMAGE_CDN = "https://cdn-images.innstant-servers.com";
const HUBWAYZ_IMAGE_SIZE = "800x600";

function moneyAmount(node) {
  if (!node || typeof node !== "object") return null;
  const n = Number(node.amount);
  return Number.isFinite(n) ? n : null;
}

function absolutizeHubwayzImageUrl(raw) {
  const path = String(raw || "").trim().replace(/^\/+/, "");
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${HUBWAYZ_IMAGE_CDN}/${HUBWAYZ_IMAGE_SIZE}/${path}`;
}

function boardLabelHe(code) {
  const label = displayBoardBasisHe(code);
  return label || "";
}

function legFromPackage(leg) {
  return normalizeUnifiedFlightLeg(leg);
}

function flightPricePerPerson(offer, pax) {
  const pp = Number(offer?.pricePerPerson ?? offer?.priceClientPerPersonUsd);
  if (Number.isFinite(pp) && pp > 0) return pp;
  const total = Number(offer?.totalFare ?? offer?.rawTotalFare);
  if (Number.isFinite(total) && total > 0) return total / Math.max(1, pax);
  return 0;
}

function pushImageUrl(urls, raw) {
  const url = absolutizeHubwayzImageUrl(
    typeof raw === "string" ? raw : raw?.url || raw?.src
  );
  if (url && !urls.includes(url)) urls.push(url);
}

function collectImageList(urls, list) {
  if (!Array.isArray(list)) return;
  list.forEach((item) => pushImageUrl(urls, item));
}

/** Room photos from the offer item. Empty when the supplier sent none. */
export function photosFromOfferItems(items) {
  const urls = [];
  for (const item of items || []) {
    collectImageList(urls, item?.images);
    collectImageList(urls, item?.photos);
    collectImageList(urls, item?.roomImages);
    pushImageUrl(urls, item?.image);
    pushImageUrl(urls, item?.imageUrl);
  }
  return urls;
}

/**
 * Room card images. Room photos win. Otherwise the hotel gallery,
 * so the room row still shows the hotel image. Empty only when both are missing.
 */
export function roomCardPhotos(room, hotelGallery) {
  const own = Array.isArray(room?.photos) ? room.photos.filter(Boolean) : [];
  if (own.length) return own;
  return Array.isArray(hotelGallery) ? hotelGallery.filter(Boolean) : [];
}

/** Every distinct supplier image with a usable URL. No display cap. */
export function buildGallery(staticHotel, fallbackImageUrl) {
  const images = Array.isArray(staticHotel?.images) ? staticHotel.images : [];
  const urls = [];
  const mainId =
    staticHotel?.mainImageId != null ? String(staticHotel.mainImageId) : null;
  const ordered = [...images].sort((a, b) => {
    const aMain = mainId && String(a?.id) === mainId ? 0 : 1;
    const bMain = mainId && String(b?.id) === mainId ? 0 : 1;
    return aMain - bMain;
  });
  for (const img of ordered) {
    const url = absolutizeHubwayzImageUrl(
      typeof img === "string" ? img : img?.url || img?.src
    );
    if (url && !urls.includes(url)) urls.push(url);
  }
  if (!urls.length && fallbackImageUrl) urls.push(fallbackImageUrl);
  return urls;
}

function roomOccupancyFromItem(item) {
  const pax = item?.pax && typeof item.pax === "object" ? item.pax : {};
  const adults = Math.max(0, Number(pax.adults) || 0);
  const childRaw = pax.children;
  let childAges = [];
  let children = 0;
  if (Array.isArray(childRaw)) {
    childAges = childRaw
      .map((c) => {
        if (typeof c === "number" && Number.isFinite(c)) return c;
        if (c && typeof c === "object") {
          const age = Number(c.age ?? c.Age);
          return Number.isFinite(age) ? age : null;
        }
        const n = Number(c);
        return Number.isFinite(n) ? n : null;
      })
      .filter((a) => a != null);
    children = childAges.length || childRaw.length;
  } else {
    children = Math.max(0, Number(childRaw) || 0);
  }
  return { adults, children, childAges };
}

const HE_MONTHS = [
  "ינואר",
  "פברואר",
  "מרץ",
  "אפריל",
  "מאי",
  "יוני",
  "יולי",
  "אוגוסט",
  "ספטמבר",
  "אוקטובר",
  "נובמבר",
  "דצמבר",
];

function cancellationTypeOf(cxl) {
  return String(
    cxl?.type || cxl?.cancellation_type || cxl?.cancellationType || ""
  ).toLowerCase();
}

function cancellationFramesOf(cxl) {
  const raw = cxl?.frames ?? cxl?.Frames;
  return Array.isArray(raw) ? raw : [];
}

function framePenaltyAmount(frame) {
  const p = frame?.penalty ?? frame?.Penalty;
  if (p == null || p === "") return null;
  if (typeof p === "number") return Number.isFinite(p) ? p : null;
  if (typeof p === "string" && p.trim() !== "" && Number.isFinite(Number(p))) {
    return Number(p);
  }
  if (typeof p === "object") {
    const n = Number(p.amount ?? p.Amount ?? p.value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function framePenaltyCurrency(frame) {
  const p = frame?.penalty ?? frame?.Penalty;
  if (!p || typeof p !== "object") return null;
  const c = p.currency ?? p.Currency;
  return c != null && String(c).trim() ? String(c).trim() : null;
}

function explicitFreeDeadline(cxl) {
  const raw =
    cxl?.cancellation_deadline ??
    cxl?.cancellationDeadline ??
    cxl?.free_cancel_until ??
    cxl?.freeCancelUntil ??
    null;
  if (raw == null || String(raw).trim() === "") return null;
  return String(raw).trim();
}

/** Supplier wall-clock date only. Unparseable values stay as received. */
export function formatHubwayzDeadlineHe(raw) {
  if (raw == null || String(raw).trim() === "") return null;
  const s = String(raw).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return s;
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return s;
  return `${day} ב${HE_MONTHS[month - 1]} ${m[1]}`;
}

function penaltyIsFullAmount(amount, currency, totals) {
  if (!Number.isFinite(amount) || amount <= 0) return false;
  const list = Array.isArray(totals) ? totals : [];
  return list.some((t) => {
    const total = Number(t?.amount);
    if (!Number.isFinite(total) || total <= 0) return false;
    const offerCur = t?.currency ? String(t.currency).toUpperCase() : "";
    const penCur = currency ? String(currency).toUpperCase() : "";
    if (offerCur && penCur && offerCur !== penCur) return false;
    return amount + 0.009 >= total;
  });
}

/**
 * Free cancellation: a 0 penalty until a deadline, or a fully-refundable
 * offer with no contradicting full-price penalty. Never invents a date.
 */
export function resolveHubwayzFreeCancellation(cxl, totals = []) {
  if (!cxl || typeof cxl !== "object") return { free: false, deadlineRaw: null };
  const type = cancellationTypeOf(cxl);
  if (isNonRefundableType(type)) return { free: false, deadlineRaw: null };

  const frames = cancellationFramesOf(cxl);
  const zero = frames.filter((fr) => framePenaltyAmount(fr) === 0);
  const positive = frames.filter((fr) => {
    const amt = framePenaltyAmount(fr);
    return amt != null && amt > 0;
  });
  const fullOnly =
    frames.length > 0 &&
    zero.length === 0 &&
    positive.length > 0 &&
    positive.every((fr) =>
      penaltyIsFullAmount(framePenaltyAmount(fr), framePenaltyCurrency(fr), totals)
    );
  if (fullOnly) return { free: false, deadlineRaw: null };

  if (zero.length) {
    const withTo = zero.find((fr) => String(fr?.to || fr?.To || "").trim());
    const deadlineRaw = withTo
      ? String(withTo.to || withTo.To).trim()
      : explicitFreeDeadline(cxl);
    return { free: true, deadlineRaw: deadlineRaw || null };
  }

  const explicit = explicitFreeDeadline(cxl);
  const fully = type.includes("fully") && type.includes("refund");
  const partial = type.includes("partial");
  const refundable = type.includes("refund") && !partial;
  if (explicit && refundable) return { free: true, deadlineRaw: explicit };
  if (fully && frames.length === 0) return { free: true, deadlineRaw: null };
  return { free: false, deadlineRaw: null };
}

function cancellationLabelHe(cxl, totals) {
  if (!cxl || typeof cxl !== "object") return null;
  const free = resolveHubwayzFreeCancellation(cxl, totals);
  if (free.free) {
    return formatInternalFreeCancellationLabel(free.deadlineRaw);
  }
  const t = cancellationTypeOf(cxl);
  const frames = cancellationFramesOf(cxl);
  if (!t && !frames.length) return null;
  if (isNonRefundableType(t)) return "לא ניתן לביטול";
  const positive = frames.filter((fr) => {
    const amt = framePenaltyAmount(fr);
    return amt != null && amt > 0;
  });
  if (
    positive.length > 0 &&
    positive.every((fr) =>
      penaltyIsFullAmount(framePenaltyAmount(fr), framePenaltyCurrency(fr), totals)
    )
  ) {
    return "לא ניתן לביטול";
  }
  if (t.includes("refund")) return "ניתן לביטול";
  return t ? String(cxl.type || cxl.cancellation_type || "") : null;
}

function isNonRefundableType(type) {
  const t = String(type || "").toLowerCase();
  return t.includes("non") && t.includes("refund");
}

/** Board summary for a combination — never invents boards. */
export function summarizeCombinationBoards(combinationRooms) {
  const labels = (combinationRooms || [])
    .map((r) => String(r?.boardLabel || "").trim())
    .filter(Boolean);
  const unique = [...new Set(labels)];
  if (!unique.length) {
    return { mixed: false, summary: null, labels: [] };
  }
  if (unique.length === 1) {
    return { mixed: false, summary: unique[0], labels: unique };
  }
  return {
    mixed: true,
    summary: "בסיסי אירוח שונים",
    labels: unique,
  };
}

function cancellationSourceForLabel(offerCxl, combinationRooms, onlyType) {
  const only = String(onlyType || "").toLowerCase();
  const offerType = cancellationTypeOf(offerCxl);
  const roomMatch = (combinationRooms || []).find((r) => {
    const t = String(r?.cancellationType || r?.cancellation?.type || "").toLowerCase();
    return t === only && r?.cancellation;
  });
  const offerFrames = cancellationFramesOf(offerCxl);
  const roomFrames = cancellationFramesOf(roomMatch?.cancellation);
  if (offerCxl && offerType === only && offerFrames.length) return offerCxl;
  if (roomFrames.length) return roomMatch.cancellation;
  if (offerCxl && offerType === only) return offerCxl;
  if (roomMatch?.cancellation) return roomMatch.cancellation;
  return offerCxl || { type: onlyType };
}

/**
 * Cancellation summary for a combination.
 * Never labels the combo free or fully refundable if any room is non-refundable.
 */
export function summarizeCombinationCancellation(offerCxl, combinationRooms, totals) {
  const roomTypes = (combinationRooms || [])
    .map((r) => r?.cancellationType || r?.cancellation?.type)
    .filter(Boolean)
    .map((t) => String(t));
  const offerType = offerCxl?.type != null ? String(offerCxl.type) : null;
  const allTypes = roomTypes.length ? roomTypes : offerType ? [offerType] : [];
  if (!allTypes.length && !cancellationFramesOf(offerCxl).length) {
    return {
      mixed: false,
      summary: null,
      label: null,
      free: false,
      fullyRefundable: false,
      types: [],
      supplierDeadline: null,
    };
  }
  const unique = [...new Set(allTypes.map((t) => t.toLowerCase()))];
  const anyNon = allTypes.some((t) => isNonRefundableType(t));
  const mixed = unique.length > 1;
  if (mixed) {
    return {
      mixed: true,
      summary: "תנאי ביטול משתנים בין החדרים",
      label: "תנאי ביטול משתנים בין החדרים",
      free: false,
      fullyRefundable: false,
      types: allTypes,
      supplierDeadline: null,
    };
  }
  if (anyNon) {
    const blocked = cancellationLabelHe(
      cancellationSourceForLabel(offerCxl, combinationRooms, allTypes[0]),
      totals
    );
    return {
      mixed: false,
      summary: blocked,
      label: blocked,
      free: false,
      fullyRefundable: false,
      types: allTypes,
      supplierDeadline: null,
    };
  }
  const only = allTypes[0];
  const source = cancellationSourceForLabel(offerCxl, combinationRooms, only);
  const free = resolveHubwayzFreeCancellation(source, totals);
  const label = cancellationLabelHe(source, totals);
  return {
    mixed: false,
    summary: label,
    label,
    free: free.free && !!free.deadlineRaw && !!label,
    fullyRefundable: !anyNon && String(only || "").toLowerCase().includes("fully"),
    types: allTypes,
    supplierDeadline: free.deadlineRaw || null,
  };
}

/**
 * One Hubwayz search result code = one bookable combination (all rooms under items[]).
 * Incomplete combinations (items.length !== requestedRoomCount) are excluded when
 * requestedRoomCount > 1 — never merge unrelated codes.
 */
export function buildRoomsFromHubwayz(
  hubwayzData,
  hotelId,
  pax,
  requestedRoomCount = 1
) {
  const results = Array.isArray(hubwayzData?.results) ? hubwayzData.results : [];
  const hid = String(hotelId);
  const needRooms = Math.max(1, Number(requestedRoomCount) || 1);
  const rooms = [];

  for (const offer of results) {
    const code = offer?.code != null ? String(offer.code).trim() : "";
    if (!code) continue;
    const items = Array.isArray(offer?.items) ? offer.items : [];
    const first = items[0] || {};
    if (String(first.hotelId) !== hid) continue;
    if (needRooms > 1 && items.length !== needRooms) continue;
    if (needRooms === 1 && items.length < 1) continue;

    const supplierNetAmount = moneyAmount(offer.netPrice);
    const supplierNetCurrency = String(offer?.netPrice?.currency || "")
      .trim()
      .toUpperCase();
    const supplierListedAmount = moneyAmount(offer.price);
    const supplierListedCurrency = String(offer?.price?.currency || "")
      .trim()
      .toUpperCase();
    const hasSupplierNet =
      Number.isFinite(supplierNetAmount) &&
      supplierNetAmount > 0 &&
      !!supplierNetCurrency;
    if (!hasSupplierNet) continue;
    const net = supplierNetAmount;
    const currency = supplierNetCurrency;

    const combinationRooms = items.map((it, idx) => {
      const occ = roomOccupancyFromItem(it);
      const boardCode = it?.board || it?.boardName || first.board || "";
      const itemCxl =
        (it?.cancellation && typeof it.cancellation === "object"
          ? it.cancellation
          : null) || null;
      const itemPrice =
        moneyAmount(it?.netPrice) ?? moneyAmount(it?.price) ?? null;
      const itemTotals = [];
      if (itemPrice != null) {
        itemTotals.push({
          amount: itemPrice,
          currency: it?.netPrice?.currency || it?.price?.currency || null,
        });
      }
      const agesLabel =
        occ.childAges.length > 0
          ? `גילאים ${occ.childAges.join(", ")}`
          : null;
      const beddingValues = readExplicitBedding(it);
      return {
        roomIndex: idx + 1,
        name:
          it?.name ||
          [it?.category, it?.bedding].filter(Boolean).join(" · ") ||
          `חדר ${idx + 1}`,
        boardLabel: boardLabelHe(boardCode),
        boardCode: String(boardCode || "").toUpperCase(),
        bedding: beddingValues[0] || "",
        beddingLabel: formatSupplierBeddingLabel(beddingValues),
        category: it?.category || "",
        adults: occ.adults,
        children: occ.children,
        childAges: occ.childAges,
        occupancyLabel:
          occ.children > 0
            ? `${occ.adults} מבוגר · ${occ.children} ילדים${
                agesLabel ? ` (${agesLabel})` : ""
              }`
            : `${occ.adults || 1} מבוגר`,
        cancellation: itemCxl,
        cancellationType: itemCxl?.type || null,
        cancellationLabel: cancellationLabelHe(itemCxl, itemTotals),
        // Secondary only when supplier provides per-item amount (never invented).
        itemPriceFx: itemPrice,
        itemCurrency:
          it?.netPrice?.currency || it?.price?.currency || currency,
      };
    });

    const roomsInOffer = combinationRooms.length;
    const name =
      roomsInOffer > 1
        ? `חבילת ${roomsInOffer} חדרים`
        : first.name ||
          [first.category, first.bedding].filter(Boolean).join(" · ") ||
          "חדר";

    const boardMeta = summarizeCombinationBoards(combinationRooms);
    const priceTotals = [];
    if (Number.isFinite(net)) {
      priceTotals.push({
        amount: net,
        currency: offer?.netPrice?.currency || offer?.price?.currency || currency,
      });
    }
    const gross = moneyAmount(offer.price);
    if (gross != null) {
      priceTotals.push({
        amount: gross,
        currency: offer?.price?.currency || currency,
      });
    }
    const cxlMeta = summarizeCombinationCancellation(
      offer.cancellation || null,
      combinationRooms,
      priceTotals
    );
    const capacityLabel =
      roomsInOffer > 1
        ? `${roomsInOffer} חדרים · ${pax} אורחים`
        : `${pax} נוסעים`;

    rooms.push({
      id: code,
      hubwayzOfferCode: code,
      name,
      boardLabel: boardMeta.summary || boardLabelHe(first.board),
      boardSummary: boardMeta.summary,
      boardMixed: boardMeta.mixed,
      boardCode: first.board || "",
      bedding: combinationRooms[0]?.bedding || "",
      beddingLabel:
        roomsInOffer > 1 ? null : combinationRooms[0]?.beddingLabel || null,
      category: first.category || "",
      capacityLabel,
      adults: pax,
      children: 0,
      roomsInOffer,
      requestedRoomCount: needRooms,
      isCompleteCombination: roomsInOffer === needRooms,
      combinationRooms,
      hotelNetTotal: net,
      supplierNetAmount: hasSupplierNet ? supplierNetAmount : null,
      supplierNetCurrency: hasSupplierNet ? supplierNetCurrency : null,
      supplierListedAmount: Number.isFinite(supplierListedAmount)
        ? supplierListedAmount
        : null,
      supplierListedCurrency: supplierListedCurrency || null,
      // Display-only per-person delta vs cheapest combo — not a supplier room rate.
      netPerPerson: net / Math.max(1, pax),
      currency,
      immediateConfirm:
        String(offer.confirmation || "").toLowerCase() === "immediate",
      allowInfants: false,
      packageRate: !!offer.packageRate,
      cancellation: offer.cancellation || null,
      cancellationSummary: cxlMeta.summary,
      cancellationSupplierDeadline: cxlMeta.supplierDeadline || null,
      cancellationMixed: cxlMeta.mixed,
      cancellationFree: cxlMeta.free === true,
      cancellationFullyRefundable: cxlMeta.fullyRefundable,
      photos: photosFromOfferItems(items),
    });
  }

  rooms.sort((a, b) => a.hotelNetTotal - b.hotelNetTotal);
  const limited = rooms.slice(0, 40);
  const base = limited[0]?.hotelNetTotal ?? 0;
  return limited.map((r) => ({
    ...r,
    deltaPerPersonFx: (r.hotelNetTotal - base) / Math.max(1, pax),
    deltaPerPersonIls: null,
    // Primary price = supplier combination total (never × room count).
    roomTotalFx: r.hotelNetTotal,
    roomTotalIls: null,
  }));
}

function sortFlightsCheapest(flightOffers) {
  return [...(flightOffers || [])].sort((a, b) => {
    const pa = flightPricePerPerson(a, 2);
    const pb = flightPricePerPerson(b, 2);
    return (pa || Infinity) - (pb || Infinity);
  });
}

function buildFlightOptionsFromLive(flightOffers, pax) {
  const sorted = sortFlightsCheapest(flightOffers).slice(0, 8);
  return sorted.map((offer, idx) => {
    const { outbound, inbound } = resolveUnifiedFlightLegs({
      outbound: offer.outbound,
      inbound: offer.inbound,
      legs: offer.legs,
      cardLegRows: offer.cardLegRows,
      rawDeal: offer.rawDeal,
      offer,
    });
    const price = flightPricePerPerson(offer, pax);
    return {
      id: String(offer.id || `flight-${idx}`),
      label: idx === 0 ? "הטיסה הזולה ביותר" : `אפשרות ${idx + 1}`,
      pricePerPerson: price,
      priceLabel:
        offer.pricePerPersonDisplayLabel ||
        offer.priceDisplayLabel ||
        (price > 0 ? `${Math.round(price)}` : null),
      currency: offer.rawCurrency || "USD",
      outbound,
      inbound,
      rawDeal: offer.rawDeal || null,
    };
  });
}

function buildMockDetailVm(packageId) {
  const fromResults =
    MOCK_PACKAGES.find((p) => p.id === packageId) || MOCK_PACKAGES[0];
  const profile = MOCK_PROFILE_PACKAGE;

  const primaryOption = {
    id: "flight-primary",
    label: "טיסה מוצעת",
    pricePerPerson: 0,
    outbound:
      legFromPackage(fromResults.outbound) || profile.flightOptions[0].outbound,
    inbound:
      legFromPackage(fromResults.inbound) || profile.flightOptions[0].inbound,
  };

  const extraOptions = (profile.flightOptions || [])
    .slice(0, 2)
    .map((opt, idx) => ({
      ...opt,
      id: `flight-alt-${idx + 1}`,
      label: opt.label || `אפשרות ${idx + 2}`,
      pricePerPerson: 0,
    }))
    .filter((opt) => {
      const sameOut =
        opt.outbound?.flightNo === primaryOption.outbound?.flightNo;
      const sameIn = opt.inbound?.flightNo === primaryOption.inbound?.flightNo;
      return !(sameOut && sameIn);
    });

  return {
    id: fromResults.id,
    source: "mock",
    productType: "flight_hotel",
    productLabel: "טיסה + מלון",
    hotelId: null,
    hotelName: fromResults.hotelName,
    stars: fromResults.stars,
    city: fromResults.city,
    country: fromResults.country,
    address: profile.address,
    description: profile.description,
    boardLabel: fromResults.boardLabel || profile.boardLabel,
    nights: fromResults.nights || profile.nights,
    checkInLabel: profile.checkInLabel,
    checkOutLabel: profile.checkOutLabel,
    dateRangeLabel: fromResults.dateRangeLabel || profile.dateRangeLabel,
    currency: fromResults.currency || profile.currency,
    paxCount: 2,
    hrCommissionPerPerson: 0,
    agentNetPerPerson:
      fromResults.agentNetPerPerson ?? profile.agentNetPerPerson,
    agentNetPerPersonIls:
      fromResults.agentNetPerPersonIls ?? profile.agentNetPerPersonIls,
    immediateConfirm: !!fromResults.immediateConfirm,
    transferIncluded: !!fromResults.transferIncluded,
    baggage: fromResults.baggage || profile.baggage,
    gallery: fromResults.imageUrl
      ? [fromResults.imageUrl, ...(profile.gallery || []).slice(1)]
      : profile.gallery,
    flightOptions: [primaryOption, ...extraOptions].slice(0, 3),
    rooms: (profile.rooms || []).map((r) => ({
      ...r,
      deltaPerPersonFx: null,
      roomTotalFx: null,
    })),
    hubwayzSearchRequest: null,
  };
}

/**
 * @param {string} packageId
 * @param {{
 *   resultsCard?: object,
 *   flightOffers?: object[],
 *   hubwayzData?: { results?: any[], hotelsById?: Record<string, any> },
 *   appliedSearch?: object,
 * } | null} liveContext
 */
export function buildFlightHotelDetailVm(packageId, liveContext = null) {
  const card = liveContext?.resultsCard;
  const hubwayzData = liveContext?.hubwayzData;
  const isLive =
    card?.provider === "hubwayz" &&
    card?.hotelId != null &&
    Array.isArray(hubwayzData?.results);

  if (!isLive) {
    return buildMockDetailVm(packageId);
  }

  const applied = liveContext?.appliedSearch || {};
  const appliedRooms = Array.isArray(applied.rooms) ? applied.rooms : [];
  const requestedRoomCount = Math.max(1, appliedRooms.length || 1);
  const pax = Math.max(
    1,
    Number(card.paxCount) || Number(summarizePax(appliedRooms)) || 2
  );
  const staticHotel =
    (hubwayzData.hotelsById || {})[String(card.hotelId)] || {};
  const rooms = buildRoomsFromHubwayz(
    hubwayzData,
    card.hotelId,
    pax,
    requestedRoomCount
  );
  const displayCurrency =
    liveContext?.displayCurrency || applied.currency || rooms[0]?.currency || null;
  const annotatedRooms = rooms.map((room) => ({
    ...room,
    supplierCurrency: room.currency,
    supplierAmount: room.hotelNetTotal,
    displayCurrency: displayCurrency || room.currency,
    paymentCurrency: "ILS",
  }));
  const flightOffersIn = Array.isArray(liveContext?.flightOffers)
    ? liveContext.flightOffers
    : [];
  const hotelsOnly = flightOffersIn.length === 0;
  const flightOptions = buildFlightOptionsFromLive(flightOffersIn, pax);

  const gallery = buildGallery(staticHotel, card.imageUrl);
  const description = normalizeHotelDescription(
    staticHotel.description ||
      staticHotel.descriptions ||
      staticHotel.hotelDescription ||
      ""
  );

  const address =
    staticHotel.address ||
    [staticHotel.city, staticHotel.zip].filter(Boolean).join(", ") ||
    "";

  return {
    id: card.id,
    source: "live",
    productType: hotelsOnly ? "hotels_only" : "flight_hotel",
    productLabel: hotelsOnly ? "מלון" : "טיסה + מלון",
    hotelsOnly,
    requestedRoomCount,
    hotelId: card.hotelId,
    hotelName: card.hotelName || staticHotel.name || `Hotel ${card.hotelId}`,
    stars: card.stars || Number(staticHotel.stars) || 0,
    city: card.city || "",
    country: card.country || "",
    address,
    description,
    boardLabel: card.boardLabel,
    nights: card.nights,
    checkInLabel: applied.checkIn || "",
    checkOutLabel: applied.checkOut || "",
    dateRangeLabel: card.dateRangeLabel || applied.dateRangeLabel || "",
    currency: rooms[0]?.currency || card.currency || null,
    supplierCurrency: rooms[0]?.currency || null,
    displayCurrency: displayCurrency || rooms[0]?.currency || null,
    paymentCurrency: "ILS",
    paxCount: pax,
    hrCommissionPerPerson: Number(card.hrCommissionPerPerson) || 0,
    agentNetPerPerson: Number(card.agentNetPerPerson) || 0,
    agentNetPerPersonIls: card.agentNetPerPersonIls ?? null,
    immediateConfirm: !!card.immediateConfirm,
    transferIncluded: !!card.transferIncluded,
    baggage: card.baggage || { none: true, bullets: [] },
    gallery,
    flightOptions,
    rooms: annotatedRooms,
    hubwayzSearchRequest:
      card.hubwayzSearchRequest || liveContext?.searchRequest || null,
  };
}

function summarizePax(rooms) {
  if (!Array.isArray(rooms) || !rooms.length) return 0;
  return rooms.reduce((sum, r) => {
    const adultsRaw = r?.adults ?? r?.AdultsCount ?? r?.Adults;
    const adultsNum = Number(adultsRaw);
    const adults =
      Number.isFinite(adultsNum) && adultsNum >= 0 ? adultsNum : 0;
    const ages = r?.childAges ?? r?.KidsAges ?? r?.kidsAges ?? [];
    const kids = Array.isArray(ages) ? ages.length : 0;
    return sum + adults + kids;
  }, 0);
}

/** Continue gate helper — exported for tests. */
export function canContinueFlightHotelRoomSelection({
  selectedRoom,
  selectedFlight,
  hotelsOnly,
  flightOptionsCount = 0,
} = {}) {
  if (!selectedRoom?.hubwayzOfferCode && !selectedRoom?.id) return false;
  if (selectedRoom?.isCompleteCombination === false) return false;
  const needsFlight = !hotelsOnly && Number(flightOptionsCount) > 0;
  if (needsFlight && !selectedFlight) return false;
  return true;
}

const DESCRIPTION_PARAGRAPH_TARGET = 280;

/** Supplier copy, with paragraph breaks kept and markup removed. */
export function normalizeHotelDescription(raw) {
  if (!raw) return "";
  if (typeof raw === "string") {
    return raw
      .replace(/\r\n/g, "\n")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n\n")
      .replace(/<[^>]+>/g, " ")
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n[ \t]+/g, "\n")
      .replace(/[ \t]{2,}/g, " ")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  }
  if (Array.isArray(raw)) {
    const languageOf = (entry) =>
      String(entry?.language || entry?.lang || entry?.locale || "").toLowerCase();
    const textOf = (entry) => entry?.text || entry?.content || entry?.description || entry;
    const hebrew = raw.find(
      (entry) =>
        languageOf(entry).startsWith("he") ||
        /[\u0590-\u05FF]/.test(String(textOf(entry) || ""))
    );
    const english = raw.find((entry) => languageOf(entry).startsWith("en"));
    const hit = hebrew || english || raw[0];
    return normalizeHotelDescription(textOf(hit));
  }
  if (typeof raw === "object") {
    const hebrew = raw.he || raw.hebrew || raw.HE;
    if (/[\u0590-\u05FF]/.test(String(hebrew || ""))) {
      return normalizeHotelDescription(hebrew);
    }
    const english = raw.en || raw.english || raw.EN;
    if (english) return normalizeHotelDescription(english);
    return normalizeHotelDescription(
      raw.text || raw.content || raw.description || ""
    );
  }
  return "";
}

/** Readable paragraphs from supplier line breaks, then sentence groups. */
export function splitHotelDescriptionParagraphs(text) {
  const raw = String(text || "").trim();
  if (!raw) return [];
  const blocks = raw
    .split(/\n+/)
    .map((part) => part.trim())
    .filter(Boolean);
  const paragraphs = [];
  for (const block of blocks) {
    if (block.length <= DESCRIPTION_PARAGRAPH_TARGET) {
      paragraphs.push(block);
      continue;
    }
    const sentences = block.match(/[^.!?…]+(?:[.!?…]+|$)/g) || [block];
    let buf = "";
    for (const sentence of sentences) {
      const piece = sentence.trim();
      if (!piece) continue;
      const next = buf ? `${buf} ${piece}` : piece;
      if (buf && next.length > DESCRIPTION_PARAGRAPH_TARGET) {
        paragraphs.push(buf);
        buf = piece;
      } else {
        buf = next;
      }
    }
    if (buf) paragraphs.push(buf);
  }
  return paragraphs;
}
